import { canvas, c } from '../setup.js';
import { getOSM } from '../OverworldAnimation.js';

/**
 * TimeOfDayShader applies color tinting effects to simulate different times of day.
 * Supports: day, dus_P_Pk, night, and dawn (dusk and dawn share the same golden hour look).
 *
 * This works as a post-processing effect: it reads from the main canvas after all drawing
 * is complete, applies the effect, and writes back to the main canvas.
 */
export default class TimeOfDayShader {
    constructor() {
        // Create offscreen canvas for processing
        this.offscreenCanvas = document.createElement('canvas');
        this.offscreenCanvas.width = canvas.width;
        this.offscreenCanvas.height = canvas.height;
        this.offscreenCtx = this.offscreenCanvas.getContext('2d');

        // Copy canvas settings
        this.offscreenCtx.imageSmoothingEnabled = false;

        // Time of day settings
        // 'day' | 'dusk' | 'night' | 'dawn'
        this.currentTimeOfDay = 'day';

        // Current custom color configuration (can be set directly for interpolation)
        this.currentConfig = {
            tint: { r: 1.0, g: 1.0, b: 1.0 },
            brightness: 1.0,
            contrast: 1.0,
        };

        // Color tint configurations for each time of day
        this.timeOfDayColors = {
            day: {
                // No tint for day - natural colors
                tint: { r: 1.0, g: 1.0, b: 1.0 },
                brightness: 1.0,
                contrast: 1.0,
            },
            dusk: {
                // Warm golden/orange tint for dusk
                tint: { r: 1.2, g: 0.95, b: 0.75 },
                brightness: 0.95,
                contrast: 1.0,
            },
            dawn: {
                // Same as dusk - golden hour vibes
                tint: { r: 1.1, g: 0.86, b: 0.75 },
                brightness: 0.95,
                contrast: 1.0,
            },
            night: {
                // Cool blue tint, darker for night
                tint: { r: 0.5, g: 0.5, b: 1.4 },
                brightness: 0.6,
                contrast: 1.0,
            },
        };
    }

    /**
     * Set the current time of day
     * @param {string} timeOfDay - 'day' | 'dusk' | 'night' | 'dawn'
     */
    setTimeOfDay(timeOfDay) {
        if (['day', 'dusk', 'night', 'dawn'].includes(timeOfDay)) {
            this.currentTimeOfDay = timeOfDay;
            // Update current config to match the preset
            this.currentConfig = { ...this.timeOfDayColors[timeOfDay] };
        } else {
            console.warn(`Invalid time of day: ${timeOfDay}. Using 'day' instead.`);
            this.currentTimeOfDay = 'day';
            this.currentConfig = { ...this.timeOfDayColors.day };
        }
    }

    /**
     * Set custom color configuration directly (for interpolation)
     * @param {Object} config - { tint: {r, g, b}, brightness, contrast }
     */
    setCustomConfig(config) {
        this.currentConfig = {
            tint: { ...config.tint },
            brightness: config.brightness,
            contrast: config.contrast,
        };
    }

    /**
     * Get the current time of day
     * @returns {string}
     */
    getTimeOfDay() {
        return this.currentTimeOfDay;
    }

    /**
     * Get the color configuration for a specific time of day
     * @param {string} timeOfDay - 'day' | 'dusk' | 'night' | 'dawn'
     * @returns {Object} Color configuration object
     */
    getTimeOfDayConfig(timeOfDay) {
        const fixedTimeOfDay = getOSM().getCurrentMap().fixedTimeOfDay;
        if (fixedTimeOfDay) {
            return this.timeOfDayColors[fixedTimeOfDay];
        }
        return this.timeOfDayColors[timeOfDay] || this.timeOfDayColors.day;
    }

    /**
     * Apply the time-of-day shader effect as a post-process on the main canvas.
     * Call this after all drawing is complete.
     */
    apply() {
        if (getOSM().getCurrentMap().fixedTimeOfDay) {
            const fixedConfig = this.getTimeOfDayConfig(getOSM().getCurrentMap().fixedTimeOfDay);
            this.setCustomConfig(fixedConfig);
        }

        const config = this.currentConfig;

        // If it's effectively day (no tint, full brightness), skip processing for performance
        if (
            config.tint.r === 1.0 &&
            config.tint.g === 1.0 &&
            config.tint.b === 1.0 &&
            config.brightness === 1.0 &&
            config.contrast === 1.0
        ) {
            return;
        }

        // Copy the current canvas content to offscreen canvas
        this.offscreenCtx.drawImage(canvas, 0, 0);

        // Get image data from offscreen canvas
        const imageData = this.offscreenCtx.getImageData(
            0,
            0,
            this.offscreenCanvas.width,
            this.offscreenCanvas.height
        );
        const data = imageData.data;

        // Apply color tint and brightness adjustments
        for (let i = 0; i < data.length; i += 4) {
            // Apply tint
            data[i] = Math.min(255, data[i] * config.tint.r); // R
            data[i + 1] = Math.min(255, data[i + 1] * config.tint.g); // G
            data[i + 2] = Math.min(255, data[i + 2] * config.tint.b); // B

            // Apply brightness
            data[i] = Math.min(255, data[i] * config.brightness);
            data[i + 1] = Math.min(255, data[i + 1] * config.brightness);
            data[i + 2] = Math.min(255, data[i + 2] * config.brightness);

            // Apply contrast (simple contrast adjustment)
            if (config.contrast !== 1.0) {
                const factor = (259 * (config.contrast * 255 + 255)) / (255 * (259 - config.contrast * 255));
                data[i] = Math.min(255, Math.max(0, factor * (data[i] - 128) + 128));
                data[i + 1] = Math.min(255, Math.max(0, factor * (data[i + 1] - 128) + 128));
                data[i + 2] = Math.min(255, Math.max(0, factor * (data[i + 2] - 128) + 128));
            }
        }

        // Put the processed image data back to offscreen canvas
        this.offscreenCtx.putImageData(imageData, 0, 0);

        // Clear main canvas and draw the processed result
        c.clearRect(0, 0, canvas.width, canvas.height);
        c.drawImage(this.offscreenCanvas, 0, 0);
    }

    /**
     * Resize the offscreen canvas (call this when main canvas is resized)
     */
    resize(width, height) {
        this.offscreenCanvas.width = width;
        this.offscreenCanvas.height = height;
    }
}
